"use client"

import { useEffect, useRef } from "react"
import { cn } from "@/lib/utils"

type Action = { at: number; pos: number }

// green(slow) → yellow → red(fast)
function speedToColor(t: number): [number, number, number] {
  if (t < 0.5) {
    const u = t / 0.5
    return [Math.round(220 * u), Math.round(160 + 80 * u), 20]
  }
  const u = (t - 0.5) / 0.5
  return [255, Math.round(200 * (1 - u)), 20]
}

// 1000 pos/sec = full stroke in 100ms (very intense) → red
// 500 pos/sec = full stroke in 200ms → orange/yellow
// 200 pos/sec = full stroke in 500ms → green
// Raised from 500 so typical scripts show meaningful color variation instead of all-red
const MAX_SPEED_UNITS_PER_SEC = 1000

interface ScriptHeatmapProps {
  actions: Action[]
  currentTime?: number  // seconds — draws a playhead if provided
  className?: string
}

export function ScriptHeatmap({ actions, currentTime, className }: ScriptHeatmapProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  // Offscreen canvas holds the static color bands — recomputed only when actions change
  const offscreenRef = useRef<OffscreenCanvas | HTMLCanvasElement | null>(null)

  // Rebuild offscreen whenever actions change
  useEffect(() => {
    if (actions.length < 2) { offscreenRef.current = null; return }

    const W = 800, H = 20
    let off: OffscreenCanvas | HTMLCanvasElement
    try {
      off = new OffscreenCanvas(W, H)
    } catch {
      // Safari < 17 fallback
      const c = document.createElement("canvas")
      c.width = W; c.height = H
      off = c
    }
    const ctx = off.getContext("2d") as CanvasRenderingContext2D | null
    if (!ctx) return

    const totalDuration = actions[actions.length - 1].at || 1
    for (let i = 0; i < actions.length - 1; i++) {
      const a = actions[i], b = actions[i + 1]
      const dt = b.at - a.at
      const speed = dt > 0 ? (Math.abs(b.pos - a.pos) / dt) * 1000 : 0
      const t = Math.min(speed / MAX_SPEED_UNITS_PER_SEC, 1)
      const [r, g, bl] = speedToColor(t)
      ctx.fillStyle = `rgb(${r},${g},${bl})`
      const x = (a.at / totalDuration) * W
      const w = Math.max(1, ((b.at - a.at) / totalDuration) * W)
      ctx.fillRect(x, 0, w, H)
    }

    offscreenRef.current = off
  }, [actions]) // eslint-disable-line react-hooks/exhaustive-deps

  // Composite: blit offscreen + playhead on every currentTime tick
  useEffect(() => {
    const canvas = canvasRef.current
    const off = offscreenRef.current
    if (!canvas) return
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const { width, height } = canvas
    ctx.clearRect(0, 0, width, height)

    if (off) {
      ctx.drawImage(off as CanvasImageSource, 0, 0, width, height)
    } else {
      ctx.fillStyle = "rgba(255,255,255,0.05)"
      ctx.fillRect(0, 0, width, height)
    }

    if (currentTime !== undefined && actions.length > 0) {
      const totalDuration = actions[actions.length - 1].at || 1
      const progress = Math.min((currentTime * 1000) / totalDuration, 1)

      // Dim the played region
      ctx.fillStyle = "rgba(0,0,0,0.45)"
      ctx.fillRect(0, 0, progress * width, height)

      // Playhead line
      const px = progress * width
      ctx.beginPath()
      ctx.strokeStyle = "rgba(255,255,255,0.9)"
      ctx.lineWidth = 1.5
      ctx.moveTo(px, 0)
      ctx.lineTo(px, height)
      ctx.stroke()
    }
  }, [actions, currentTime])

  return (
    <canvas
      ref={canvasRef}
      width={800}
      height={20}
      className={cn("w-full block", className)}
    />
  )
}
